/**
 * PATH: packages/ui/src/components/overlayChromeStore.ts
 * FILE_ID: UI-CHROME-OVERLAY-001
 * PURPOSE: Tiny reference-counted pub/sub so any component that opens
 *          a heavy right-pane / drawer can register itself, and the
 *          bottom floating chrome (UniversalDock, ComposeDock, future
 *          right-rail toolbars) can hide while the overlay claims the
 *          viewport.
 * WHY: Operator complaint 2026-05-19 — "see how the bottom of this
 *      with feedback teams and ai chat overlaps you can even hide them
 *      here.. I thought we implemented this universally across
 *      everything ages ago". They were right that the abstraction
 *      should exist; they were wrong that it already did. Today the
 *      Kanban CardDetailDrawer only publishes a CSS variable that
 *      *shifts* the dock left, it never hides it. That leaves the
 *      Feedback / Teams / AI Chat pill poking into the drawer's
 *      content region whenever the drawer is wide enough to occupy
 *      the bottom-right corner of the viewport (every pinned-rail
 *      operator). This module is the universal hide-when-occluded
 *      hook the operator believed already existed.
 * FLOW:
 *   ┌──────────────────────┐    acquireOverlay()    ┌─────────────────┐
 *   │ CardDetailDrawer     │ ──────────────────────▶│ activeCount + 1 │
 *   │ EmailPreviewPane     │                        │   ↓             │
 *   │ <other right-pane>   │                        │ listeners.fire  │
 *   └──────────────────────┘                        └─────────────────┘
 *   ┌──────────────────────┐    subscribeOverlay()  ┌─────────────────┐
 *   │ UniversalDock        │ ◀──────────────────────│ active boolean  │
 *   │ ComposeDock          │                        └─────────────────┘
 *   └──────────────────────┘
 * DEPENDENCIES: none — module-scope state + a Set listener registry.
 *               Deliberately framework-free (no React, no Zustand)
 *               so consumers in any package can call into it without
 *               adding a dependency.
 * RELATED:
 *   - UniversalDock.tsx: consumes via subscribeOverlay()
 *   - CardDetailDrawer.tsx: calls acquireOverlay() while open
 */

type Listener = (active: boolean) => void

// Module-scope mutable state. The reference count is intentional —
// two overlays can be open at once (e.g. CardDetailDrawer + a confirm
// dialog) and we only want to un-hide the chrome when ALL of them
// release. The Set is used so the same listener can't double-subscribe
// (UniversalDock can re-mount during HMR without leaking).
let activeCount = 0
const listeners: Set<Listener> = new Set()

function notify(): void {
  // Snapshot the listeners before iterating so a listener that
  // unsubscribes itself mid-fire doesn't perturb the iteration order.
  const snapshot = Array.from(listeners)
  const active = activeCount > 0
  for (const l of snapshot) {
    try {
      l(active)
    } catch {
      // A misbehaving listener must not block the others.
    }
  }
}

/**
 * Acquire an overlay slot. Increments the active count and fires
 * listeners with `true` when this is the FIRST overlay (count went
 * from 0 → 1). Returns a release function that decrements the count
 * and fires listeners with `false` when the LAST overlay releases
 * (count went from 1 → 0).
 *
 * Use inside `useEffect`:
 *
 *     useEffect(() => {
 *       if (!open) return
 *       const release = acquireOverlay()
 *       return release
 *     }, [open])
 *
 * The release function is idempotent — calling it twice is a no-op,
 * so the cleanup form above is safe even if React invokes it more
 * than once in dev StrictMode.
 */
export function acquireOverlay(): () => void {
  activeCount += 1
  if (activeCount === 1) notify()
  let released = false
  return () => {
    if (released) return
    released = true
    activeCount = Math.max(0, activeCount - 1)
    if (activeCount === 0) notify()
  }
}

/**
 * Subscribe to overlay-active changes. The listener is invoked
 * immediately with the CURRENT state so the subscriber doesn't need
 * to call `isOverlayActive()` separately. Returns an unsubscribe
 * function.
 *
 * Use inside `useEffect`:
 *
 *     const [hidden, setHidden] = useState(isOverlayActive())
 *     useEffect(() => subscribeOverlay(setHidden), [])
 */
export function subscribeOverlay(listener: Listener): () => void {
  listeners.add(listener)
  // Fire once with the current state so first-mount renders are
  // correct without forcing every consumer to also call
  // `isOverlayActive()`.
  try {
    listener(activeCount > 0)
  } catch {
    // Ignore — a throwing listener on first-fire shouldn't poison
    // the registry.
  }
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Read the current overlay state synchronously. Cheap (just a
 * number compare). Useful for component state initialisers that
 * want to pin the right value on first mount before the
 * `subscribeOverlay` effect runs.
 */
export function isOverlayActive(): boolean {
  return activeCount > 0
}

/**
 * Test-only reset. NOT for production code — the registry is
 * module-scoped and intentionally long-lived. Exported here so
 * Vitest can clear state between tests without HMR-style trickery.
 */
export function __resetOverlayChromeStoreForTests(): void {
  activeCount = 0
  listeners.clear()
}
